import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { SectionHeader, DataCard } from "../components/UI";
import { TID } from "../constants/testIds";
import { Plus, Building2, Globe, Sparkles, ArrowRight } from "lucide-react";

export default function SuperDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/super-admin/stats").then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <SectionHeader
        title="PropHR · Root Console"
        subtitle="Super Admin mode. All tenants, cross-tenant metrics, and Enterprise Intelligence are available."
        right={<Link to="/super/tenants" className="btn-primary" data-testid={TID.createTenantBtn}><Plus size={16} strokeWidth={2}/> Onboard tenant</Link>}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <DataCard label="Tenants" value={stats?.total_tenants ?? ","} testid="s-tenants" />
        <DataCard label="Active" value={stats?.active_tenants ?? ","} accent testid="s-active-tenants"/>
        <DataCard label="Users" value={stats?.total_users ?? ","} testid="s-users"/>
        <DataCard label="Cases" value={stats?.total_cases ?? ","} testid="s-cases"/>
        <DataCard label="Open" value={stats?.open_cases ?? ","} testid="s-open" sublabel="ACROSS ALL TENANTS"/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 data-cell p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow flex items-center gap-2"><Building2 size={12} strokeWidth={2}/> Tenant Portfolio</div>
            <Link to="/super/tenants" className="text-xs text-blue-700 hover:underline">Manage →</Link>
          </div>
          {stats?.tenants?.length ? (
            <div className="border border-slate-200 overflow-x-auto rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-left">
                  <tr>
                    <th className="px-4 py-2 eyebrow">Tenant</th>
                    <th className="px-4 py-2 eyebrow">Plan</th>
                    <th className="px-4 py-2 eyebrow">Status</th>
                    <th className="px-4 py-2 eyebrow">Users</th>
                    <th className="px-4 py-2 eyebrow">Cases</th>
                    <th className="px-4 py-2 eyebrow">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.tenants.map((t) => (
                    <tr key={t.id} className="row-hover border-b border-slate-100 last:border-b-0">
                      <td className="px-4 py-2 font-medium">{t.name}</td>
                      <td className="px-4 py-2 text-xs font-mono uppercase">{t.plan}</td>
                      <td className="px-4 py-2"><span className={"pill " + (t.status === "active" ? "pill-resolved" : "pill-closed")}>{t.status}</span></td>
                      <td className="px-4 py-2 font-mono">{t.users}</td>
                      <td className="px-4 py-2 font-mono">{t.total_cases}</td>
                      <td className="px-4 py-2 font-mono text-blue-700">{t.open_cases}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-slate-500 py-8 text-center font-mono">No tenants yet.</div>
          )}
        </div>

        <div className="ai-surface tracing-beam p-6">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={16} strokeWidth={1.75} className="text-blue-400"/>
            <span className="eyebrow text-blue-300">Enterprise Intelligence</span>
          </div>
          <div className="font-editorial text-xl text-slate-100 leading-snug mb-5">
            "Reason across your entire PropHR portfolio , hotspots, systemic risks, board-ready synthesis."
          </div>
          <Link to="/super/enterprise-intelligence" className="btn-ghost text-xs w-full" data-testid={TID.godModeRunBtn}>
            <Sparkles size={14} strokeWidth={2}/> Open Enterprise Intelligence <ArrowRight size={12} className="ml-auto"/>
          </Link>
        </div>
      </div>
    </div>
  );
}
